const DEFAULT_BRAND = '#1A428A';
/** Darker navy — pairs with sidebar brand for clear chart contrast. */
const BRAND_DARK = '#0F2F66';
/** Brighter blue — distinct from sidebar, not the old electric #0B5ED7. */
const BRAND_BRIGHT = '#4F7EC8';

/**
 * Mixed chart palette:
 * sidebar brand, darker navy, accent orange/teal, brighter blue, slate.
 * Avoids two near-identical blues in a row (as with the old #0B5ED7 / #1E67D6).
 */
const DEFAULT_CHART_PALETTE_COLORS = [
  DEFAULT_BRAND,
  BRAND_DARK,
  '#F97316',
  '#0EA5A4',
  BRAND_BRIGHT,
  '#94A3B8',
];

function isDefaultBrand(primary) {
  return normalizeHex(primary).toUpperCase() === normalizeHex(DEFAULT_BRAND).toUpperCase();
}

function normalizeHex(hex) {
  const raw = String(hex || DEFAULT_BRAND).replace('#', '');
  if (raw.length === 3) {
    return raw
      .split('')
      .map((char) => char + char)
      .join('');
  }
  return raw.slice(0, 6);
}

function hexToRgb(hex) {
  const normalized = normalizeHex(hex);
  return {
    r: parseInt(normalized.slice(0, 2), 16),
    g: parseInt(normalized.slice(2, 4), 16),
    b: parseInt(normalized.slice(4, 6), 16),
  };
}

function rgbToHex(r, g, b) {
  const channel = (value) =>
    Math.round(Math.max(0, Math.min(255, value)))
      .toString(16)
      .padStart(2, '0');
  return `#${channel(r)}${channel(g)}${channel(b)}`;
}

function mixRgb(source, target, weight) {
  const ratio = Math.max(0, Math.min(1, weight));
  return {
    r: source.r + (target.r - source.r) * ratio,
    g: source.g + (target.g - source.g) * ratio,
    b: source.b + (target.b - source.b) * ratio,
  };
}

function mixHex(base, target, weight) {
  const mixed = mixRgb(hexToRgb(base), hexToRgb(target), weight);
  return rgbToHex(mixed.r, mixed.g, mixed.b);
}

export function withAlpha(hex, alpha) {
  const { r, g, b } = hexToRgb(hex);
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
}

const CHART_ACCENT_ORANGE = '#F97316';
const CHART_ACCENT_TEAL = '#0EA5A4';
const CHART_ACCENT_SLATE = '#94A3B8';

export function buildChartPalette(primary = DEFAULT_BRAND, count = 6) {
  if (isDefaultBrand(primary)) {
    return DEFAULT_CHART_PALETTE_COLORS.slice(0, count);
  }

  const brand = normalizeHex(primary).length === 6 ? `#${normalizeHex(primary)}` : DEFAULT_BRAND;
  const brandDark = mixHex(brand, '#000000', 0.32);
  const brandBright = mixHex(brand, '#ffffff', 0.28);

  const palette = [
    brand,
    brandDark,
    CHART_ACCENT_ORANGE,
    CHART_ACCENT_TEAL,
    brandBright,
    CHART_ACCENT_SLATE,
  ];

  return palette.slice(0, count);
}

/**
 * Time-series (Revenue / AOV) line charts — always match the sidebar bg
 * (`--brand-primary`). Ranking charts use `buildChartPalette` instead.
 */
export function getLineChartColors(primary = DEFAULT_BRAND) {
  const brand =
    normalizeHex(primary).length === 6 ? `#${normalizeHex(primary)}` : DEFAULT_BRAND;

  return {
    line: brand,
    primary: brand,
    fillTop: withAlpha(brand, 0.28),
    fillBottom: withAlpha(brand, 0.02),
    sliderFill: withAlpha(brand, 0.12),
    handle: brand,
    tooltipAccent: brand,
    cursorFill: withAlpha(brand, 0.06),
  };
}

export const DEFAULT_CHART_PALETTE = DEFAULT_CHART_PALETTE_COLORS;
